import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, CheckCircle, ArrowRight } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSessionExpired = searchParams.get('session') === 'expired';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else if (result.error === 'EMAIL_NOT_VERIFIED') {
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <Helmet>
        <title>Masuk — RencanaGuru.com</title>
        <meta name="description" content="Login ke akun RencanaGuru dan lanjutkan membuat perangkat pembelajaran berkualitas dengan AI." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #1D4ED8 60%, #3B82F6 100%)' }}>
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #FCD34D 0%, transparent 50%), radial-gradient(circle at 80% 20%, #FFFFFF 0%, transparent 50%)' }} />

        {/* Logo */}
        <div className="relative flex items-center gap-4">
          <img src="/logo-white.png" alt="RencanaGuru" className="h-16 w-16 object-contain drop-shadow-lg" />
          <div>
            <div className="font-bold text-2xl text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-400">.com</span>
            </div>
            <div className="text-blue-300 text-xs font-medium">Platform AI untuk Guru Indonesia</div>
          </div>
        </div>

        {/* Middle content */}
        <div className="relative flex flex-col gap-8">
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Selamat Datang<br />Kembali, Guru Hebat!
            </h2>
            <p className="text-blue-200 text-lg leading-relaxed">
              Masuk ke akun Anda dan lanjutkan membuat perangkat pembelajaran berkualitas dengan bantuan AI.
            </p>
          </div>

          {/* Feature list */}
          <div className="flex flex-col gap-4">
            {[
              'Generate Modul Ajar Kurikulum Merdeka dalam 5 menit',
              'Sesuai Kurikulum Merdeka & Permendikbudristek',
              'Export ke Word & Excel siap cetak',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={14} className="text-yellow-900" />
                </div>
                <span className="text-blue-100 text-sm">{item}</span>
              </div>
            ))}
          </div>

          {/* Testimonial */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
            <p className="text-blue-100 text-sm italic leading-relaxed mb-3">
              "Rencana Guru menghemat waktu saya lebih dari 10 jam per minggu. Modul Ajar langsung jadi dan sesuai format!"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-300 flex items-center justify-center text-lg">👩‍🏫</div>
              <div>
                <div className="text-white text-sm font-semibold">Sari Wahyuni, S.Pd</div>
                <div className="text-blue-300 text-xs">Guru IPA · SMPN 3 Surabaya</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="relative text-blue-300 text-xs">
          © 2026 RencanaGuru.com
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
        style={{ background: 'linear-gradient(160deg, #EEF2FF 0%, #F0FDF4 100%)' }}>
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center gap-2 mb-8">
            <img src="/logo.png" alt="RencanaGuru" className="h-16 w-16 object-contain" />
            <span className="font-bold text-xl text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              RencanaGuru<span className="text-yellow-500">.com</span>
            </span>
            <span className="text-slate-400 text-xs">Platform AI untuk Guru Indonesia</span>
          </div>

          <div className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl shadow-lg p-8">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-blue-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Masuk ke Akun
              </h1>
              <p className="text-slate-500 text-sm">Belum punya akun?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-semibold">Daftar gratis</Link>
              </p>
            </div>

            {isSessionExpired && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm flex items-start gap-2">
                <span className="mt-0.5">⏰</span>
                <div>
                  <p className="font-semibold">Sesi Anda telah berakhir</p>
                  <p className="text-amber-600 text-xs mt-0.5">Silakan masuk kembali untuk melanjutkan.</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2" data-testid="error-message">
                <span className="text-red-500 mt-0.5">⚠</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email" className="text-slate-700 font-semibold text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="rounded-xl border-blue-100 bg-white focus:border-blue-500 focus:ring-blue-500 text-base py-2.5 h-11"
                  placeholder="nama@email.com"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700 font-semibold text-sm">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Lupa password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                    className="rounded-xl border-blue-100 bg-white focus:border-blue-500 focus:ring-blue-500 text-base py-2.5 h-11 pr-11"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-button"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-base shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 mt-1"
                style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Memproses...
                  </>
                ) : (
                  <>
                    Masuk
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400">atau</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Google Login */}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`}
              className="flex items-center justify-center gap-3 w-full border border-slate-300 rounded-xl py-3 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Lanjutkan dengan Google
            </a>

            <div className="mt-4 pt-4 border-t border-blue-100 text-center">
              <p className="text-xs text-slate-400">
                Dengan masuk, Anda menyetujui{' '}
                <Link to="/terms-of-service" className="text-blue-600 hover:underline">Syarat & Ketentuan</Link>
                {' '}dan{' '}
                <Link to="/privacy-policy" className="text-blue-600 hover:underline">Kebijakan Privasi</Link>
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            © 2026 RencanaGuru.com · Platform AI untuk Guru Indonesia
          </p>
        </div>
      </div>
    </div>
  );
};
